import React, { useContext } from 'react';
import Home from './areas/Home/Home';
import NavBar from './components/nav-bar/NavBar';
import Letter from './areas/Letter/Letter';
import Artist from './areas/Artist/Artist';
import Album from './areas/Album/Album';
import Admin from './areas/Admin/Admin';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from "@mui/material/styles";
import theme from './theme';
import { Container, CssBaseline } from '@mui/material';
import { Route, Routes } from 'react-router-dom';
import MusicPlayer from './components/musicPlayer/MusicPlayer';
import { AuthenticationContext } from './components/authentication/AuthenticationContext';
import Letters from './components/letters/Letters';
import EditAlbum from './areas/Album/EditAlbum';
import EditArtist from './areas/Artist/EditArtist';
import EditArtistRelations from './areas/Artist/EditArtistRelations';
import ManagePhotos from './areas/Artist/ManagePhotos';
import Genre from './areas/Genre/Genre';
import CreateGenre from './areas/Genre/CreateGenre';
import EditArtistGenres from './areas/Artist/EditArtistGenres';
import Genres from './areas/Genre/Genres';
import Compilation from './areas/Compilation/Compilation';
import EditCompilation from './areas/Compilation/EditCompilation';
import CompilationDirectories from './areas/Letter/CompilationDIrectories';
import CompilationDirectory from './areas/CompilationDirectory/CompilationDirectory';
import EditGenre from './areas/Genre/EditGenre';
import RecentTracks from './areas/History/RecentTracks';
import lastFmLogo from './assets/lastfm-logo.png';
import openApiLogo from './assets/open-api.png';
import Location from './areas/Location/Location';
import Search from './areas/Search/Search';
import Analytics from './areas/Analytics/Analytics';
import Playlists from './areas/Playlist/Playlists';
import CreatePlaylist from './areas/Playlist/CreatePlaylist';
import Playlist from './areas/Playlist/Playlist';
import PlaylistModalProvider from './contexts/playlist-modal-context/PlaylistModalProvider';
import EditPlaylist from './areas/Playlist/EditPlaylist';
import VectorModalProvider from './contexts/vector-modal-context/VectorModalProvider';
import ControlBar from './components/control-bar/ControlBar';
import Settings from './areas/Settings/Settings';
import NowPlaying from './areas/NowPlaying/NowPlaying';
import './App.scss';
import Queue from './areas/Queue/Queue';
import LyricsModalProvider from './contexts/lyrics-modal-context/LyricsModalProvider';

declare module '@mui/material/styles' {
  interface Theme {
    status: {
      danger: string;
    };
  }

  // allow configuration using `createTheme()`
  interface ThemeOptions {
    status?: {
      danger?: string;
    };
  }
}

const App = () => {  
  const queryClient = new QueryClient();
  const { isAuthenticated } = useContext(AuthenticationContext);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        <VectorModalProvider>
        <LyricsModalProvider>
        <PlaylistModalProvider>
          <QueryClientProvider client={queryClient}>
            <NavBar />
            <header className="App-header">
              <Container>                
                {isAuthenticated() &&
                  <>
                    <Letters />
                    <ControlBar />
                  </>
                }
              </Container>
            </header>
            <Container>
              <Routes>
                <Route path="/" element={<Home />} />
                {isAuthenticated() &&
                  <>
                    <Route path="/letters/:letter" element={<Letter />} />
                    <Route path="/letters/:letter/artists/:artistName" element={<Artist />} />
                    <Route path="/letters/:letter/artists/:artistName/photos" element={<ManagePhotos />} />
                    <Route path="/letters/:letter/artists/:artistName/edit" element={<EditArtist />} />
                    <Route path="/letters/:letter/artists/:artistName/similar" element={<EditArtistRelations />} />
                    <Route path="/letters/:letter/artists/:artistName/genres" element={<EditArtistGenres />} />
                    <Route path="/letters/:letter/artists/:artistName/albums/:albumName" element={<Album />} />
                    <Route path="/letters/:letter/artists/:artistName/albums/:albumName/edit" element={<EditAlbum />} />
                    <Route path="/genres" element={<Genres />} />
                    <Route path="/genres/:id" element={<Genre />} />
                    <Route path="/genres/:id/edit" element={<EditGenre />} />
                    <Route path="/genres/create" element={<CreateGenre />} />           
                    <Route path="/play/nowplaying" element={<NowPlaying />} />
                    <Route path="/play/nowplaying/genre/:genreId" element={<NowPlaying />} />
                    <Route path="/play/nowplaying/artist/:artistName" element={<NowPlaying />} />
                    <Route path="/play/nowplaying/similar/:similar" element={<NowPlaying />} />
                    <Route path="/compilations" element={<CompilationDirectories />} />
                    <Route path="/compilations/:directory" element={<CompilationDirectory />} />
                    <Route path="/compilations/:directory/albums/:albumName" element={<Compilation />} />
                    <Route path="/compilations/:directory/albums/:albumName/edit" element={<EditCompilation />} />
                    <Route path="/recentTracks" element={<RecentTracks />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/locations/country/:country?" element={<Location />} />
                    <Route path="/locations/country/:country?/stateProvidence/:stateProvidence?" element={<Location />} />
                    <Route path="/locations/country/:country?/stateProvidence/:stateProvidence?/city/:city?" element={<Location />} />
                    <Route path="/search" element={<Search />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/playlists" element={<Playlists />} />
                    <Route path="/playlists/:id" element={<Playlist />} />
                    <Route path="/playlists/create" element={<CreatePlaylist />} />
                    <Route path="/playlists/:id/edit" element={<EditPlaylist />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/queue" element={<Queue />} />
                  </>
                }
                <Route path="*" element={<Home />} />
              </Routes>
            </Container>
            <footer style={{
              color: "grey",
              position: "fixed",
              bottom: 0,
              height: '118px',
              width: '100%',
              backgroundColor: 'white',
              borderTop: '1px solid #999'
            }}>
              {isAuthenticated()
                ? <MusicPlayer />
                : <>
                    <img alt="Last FM" style={{ width: '88px', height: '88px'}} src={lastFmLogo} />
                    <img alt="Open API" style={{ width: '88px', height: '88px'}} src={openApiLogo} />
                  </>}
            </footer>
          </QueryClientProvider>
        </PlaylistModalProvider>
        </LyricsModalProvider>
        </VectorModalProvider>
      </div>
    </ThemeProvider>
  );
}

export default App;
